import { useEffect, useState } from 'react';
import type { TourAdminOverride } from './adminStore';

/** Seats of one tour (Cupos): a number for every day and/or numbers for specific dates. 0 = closed. */
export interface TourCapacity {
  perDay?: number;
  dates?: Record<string, number>;
}

/**
 * Settings edited in the admin and kept on the server (netlify/functions/site-settings): tour edits and
 * booking rules. Every visitor loads them; the last copy is kept in this browser so the next visit starts
 * with the right prices before the network answers.
 */
export interface SiteSettings {
  tourOverrides: Record<string, TourAdminOverride>;
  pixDepositPercent: number;
  advanceDays: number;
  /** Seats per tour slug, counted on the server against paid and still-payable reservations. */
  capacity?: Record<string, TourCapacity>;
  /** Minutes a visitor has to pay the Pix before the reservation is removed (default 30). */
  paymentWindowMinutes?: number;
  updatedAt?: string;
}

export const DEFAULT_PAYMENT_WINDOW_MINUTES = 30;

export function paymentWindowMinutes(settings: SiteSettings = current) {
  const value = Math.trunc(Number(settings.paymentWindowMinutes));
  return Number.isFinite(value) && value >= 10 && value <= 1440 ? value : DEFAULT_PAYMENT_WINDOW_MINUTES;
}

/** Whether the admin set a seat limit for this tour (any day or a specific date). */
export function hasSeatLimit(slug: string, settings: SiteSettings = current) {
  const target = slug.toLowerCase();
  const rule = Object.entries(settings.capacity || {}).find(([key]) => key.toLowerCase() === target)?.[1];
  if (rule && (typeof rule.perDay === 'number' || Object.keys(rule.dates || {}).length > 0)) return true;
  const legacy = Object.entries(settings.tourOverrides || {}).find(([key]) => key.toLowerCase() === target)?.[1];
  return typeof legacy?.availability === 'number';
}

const CACHE_KEY = 'aquamarina_site_settings_v1';
const DEFAULTS: SiteSettings = { tourOverrides: {}, pixDepositPercent: 20, advanceDays: 1 };
export const SETTINGS_EVENT = 'aquamarina-admin-update';

function readCache(): SiteSettings | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? { ...DEFAULTS, ...(JSON.parse(raw) as SiteSettings) } : null;
  } catch {
    return null;
  }
}

let current: SiteSettings = readCache() || DEFAULTS;
export const hasCachedSettings = () => readCache() !== null;

export function getSiteSettings(): SiteSettings {
  return current;
}

/** Replaces the settings in memory and in the cache, then tells the page to redraw. */
export function setSiteSettings(next: SiteSettings) {
  current = { ...DEFAULTS, ...next };
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(current)); } catch { /* storage blocked: memory only */ }
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(SETTINGS_EVENT));
}

/** Fetches the latest settings; keeps the cached ones when the server cannot be reached. */
export async function loadSiteSettings() {
  try {
    const response = await fetch('/.netlify/functions/site-settings', { cache: 'no-store' });
    if (!response.ok) return;
    const fresh = (await response.json()) as SiteSettings;
    if (fresh.updatedAt !== current.updatedAt || !hasCachedSettings()) setSiteSettings(fresh);
  } catch {
    // Offline or local file preview: the cached or default settings stay.
  }
}

/** Re-renders the component whenever the settings change (admin edit, fresh load). */
export function useSiteSettingsVersion() {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((value) => value + 1);
    window.addEventListener(SETTINGS_EVENT, bump);
    return () => window.removeEventListener(SETTINGS_EVENT, bump);
  }, []);
  return version;
}
